import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, Crop, Check, X, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/api';
import { toast } from 'sonner';

const PlayerPhotoCrop = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropper(true);
      setCrop(null);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    // Default to center square crop
    setCrop({
      unit: 'px',
      x, y,
      width: size,
      height: size,
    });
  }, []);

  const getCroppedBlob = () => {
    return new Promise((resolve) => {
      if (!imgRef.current || !completedCrop) { resolve(null); return; }
      const canvas = document.createElement('canvas');
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      const outputSize = 400; // 400x400 output
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');

      ctx.beginPath();
      ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(
        imgRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0, 0, outputSize, outputSize,
      );
      canvas.toBlob(resolve, 'image/png', 0.95);
    });
  };

  const handleCropConfirm = async () => {
    setUploading(true);
    try {
      const blob = await getCroppedBlob();
      if (!blob) {
        toast.error('Crop failed');
        setUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append('file', blob, 'player-photo.png');
      formData.append('tags', 'player-photo');
      const res = await api.uploadMedia(formData);
      const url = res.data.url;
      onChange(url);
      setShowCropper(false);
      setImageSrc(null);
      toast.success('Photo cropped & uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setCrop(null);
  };

  return (
    <div data-testid="player-photo-crop">
      <Label className="text-gray-300 mb-1.5 block">Player Photo</Label>

      {/* Current photo preview */}
      {value && !showCropper && (
        <div className="flex items-center gap-3 mb-2">
          <img
            src={value}
            alt="Player"
            className="w-14 h-14 rounded-full object-cover border-2 border-gray-600"
            data-testid="player-photo-preview"
          />
          <button
            type="button"
            onClick={() => { onChange(''); }}
            className="text-gray-500 hover:text-red-400 text-xs"
          >
            Remove
          </button>
        </div>
      )}

      {/* Upload button */}
      {!showCropper && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-gray-600 text-gray-300"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-photo-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {value ? 'Change Photo' : 'Upload Photo'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Crop interface */}
      {showCropper && imageSrc && (
        <div className="border border-gray-600 rounded-lg p-3 bg-gray-800/50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-300 text-xs">
              <Crop className="w-3.5 h-3.5" />
              Drag to crop — circle preview
            </div>
            <button type="button" onClick={() => {
              if (!imgRef.current) return;
              const { width, height } = imgRef.current;
              const size = Math.min(width, height);
              setCrop({ unit: 'px', x: (width - size) / 2, y: (height - size) / 2, width: size, height: size });
            }} className="text-gray-400 hover:text-white">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="max-h-64 overflow-hidden flex justify-center">
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              onComplete={c => setCompletedCrop(c)}
              aspect={1}
              circularCrop
            >
              <img
                src={imageSrc}
                onLoad={onImageLoad}
                alt="Crop"
                style={{ maxHeight: '250px', maxWidth: '100%' }}
              />
            </ReactCrop>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleCropConfirm}
              disabled={uploading || !completedCrop}
              data-testid="crop-confirm-btn"
            >
              {uploading ? 'Uploading...' : <><Check className="w-3.5 h-3.5 mr-1" /> Confirm</>}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-gray-600"
              onClick={handleCancel}
              disabled={uploading}
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerPhotoCrop;
